import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Popconfirm,
  Result,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ALIMTALK_SEND_HISTORY_LIST_URL_PATH } from '../../../../../../constants/service/common/urlConstants';
import { PERMISSION_ACTIONS, PERMISSION_SUBJECTS } from '../../../../../../constants/service/common/userPermissions';
import { useAuth } from '../../../../../../hooks/common/useAuth';
import AlimtalkSendStatusTag from '../AlimtalkSendStatusTag';
import AlimtalkSendResultDonutChart from '../AlimtalkSendResultDonutChart';
import {
  isForbiddenScreenError,
  useAlimtalkSendHistoryDetailQuery,
  useCancelAlimtalkSendMutation,
} from '../../queries/alimtalkSendHistoryQueries';
import { ALIMTALK_SEND_ERROR_CODE } from '../../../../../../constants/errorResponse/alimtalkSend';
import { AlimtalkSendFailureReason } from '../../types';
import {
  formatCoupon,
  formatCount,
  formatDateTime,
  formatSendType,
  getRefetchFailureMessage,
  isInProgressStatus,
  resolveQueryFailureView,
} from '../../utils';
import { readListSearchFromLocationState } from '../../listSearchParams';

/** 아직 제공되지 않는 기능(다운로드) 버튼 안내 문구 */
const COMING_SOON_TOOLTIP = '준비 중인 기능입니다.';

/** 실패 사유 코드별 집계 안내 */
const FAILURE_REASON_NOTICE = '코드별 집계는 실패 수와 다를 수 있으며 발송 요청 응답 기준입니다.';

const failureColumns: ColumnsType<AlimtalkSendFailureReason> = [
  { title: '코드', dataIndex: 'code', key: 'code', width: 140 },
  { title: '설명', dataIndex: 'description', key: 'description' },
  {
    title: '건수',
    dataIndex: 'count',
    key: 'count',
    width: 100,
    align: 'right',
    render: (value: number) => value.toLocaleString(),
  },
];

const AlimtalkSendHistoryDetailWrap = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const numericId = Number(id);

  const { data, isLoading, isFetching, isError, error, refetch } = useAlimtalkSendHistoryDetailQuery(numericId);
  const cancelMutation = useCancelAlimtalkSendMutation();
  const { can } = useAuth();
  // 백엔드 예약 취소 DELETE /alimtalk-sends/:id 는 update 권한을 요구한다.
  const canCancel = can(PERMISSION_ACTIONS.UPDATE, PERMISSION_SUBJECTS.ALIMTALK_SEND_SCHEDULE);

  // 목록에서 들어왔으면 그때의 검색 조건·페이지로 돌아간다 (직접 진입이면 기본 목록)
  const goToList = (): void =>
    navigate({
      pathname: ALIMTALK_SEND_HISTORY_LIST_URL_PATH,
      search: readListSearchFromLocationState(location.state),
    });

  if (!Number.isSafeInteger(numericId) || numericId <= 0) {
    return (
      <Result status="warning" title="잘못된 예약 ID 입니다." extra={<Button onClick={goToList}>목록으로</Button>} />
    );
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  /**
   *  보여줄 데이터가 없을 때(첫 로드 실패)·권한 없음(403)만 화면 전체를 에러로 바꾼다.
   *  자동 갱신·재조회만 실패했으면 마지막 성공 상세를 유지하고 경고 배너만 띄운다 (404 등도 첫 로드에서만 분기).
   */
  const failureView = resolveQueryFailureView({
    isError,
    hasData: data !== undefined,
    isForbidden: isForbiddenScreenError(error),
  });

  if (failureView === 'screen' || !data) {
    // 권한 없음(403)도 재시도해도 같은 결과라 다시 시도 버튼 없이 목록으로 버튼만 둔다
    if (isForbiddenScreenError(error)) {
      return <Result status="403" title="조회 권한이 없습니다." extra={<Button onClick={goToList}>목록으로</Button>} />;
    }
    // 없는 예약(404)은 재시도해도 같은 결과라 별도 문구만 보여주고 다시 시도 버튼은 두지 않는다
    const isNotFound = error?.status === 404 || error?.code === ALIMTALK_SEND_ERROR_CODE.ALIMTALK_SEND_008;
    if (isNotFound) {
      return (
        <Result
          status="404"
          title="존재하지 않거나 삭제된 예약입니다."
          extra={<Button onClick={goToList}>목록으로</Button>}
        />
      );
    }
    return (
      <Result
        status="error"
        title="발송 상세 정보를 불러오지 못했습니다."
        // 실패 알림은 이 화면 하나뿐이다 (모달·전역 토스트는 쿼리/API 쪽에서 끈다)
        subTitle={error?.message}
        extra={
          <Space>
            <Button type="primary" loading={isFetching} onClick={() => refetch()}>
              다시 시도
            </Button>
            <Button onClick={goToList}>목록으로</Button>
          </Space>
        }
      />
    );
  }

  const { send } = data;
  const { result } = send;

  const topActions = (
    <Space wrap>
      <Button onClick={goToList}>목록</Button>
      <Tooltip title={COMING_SOON_TOOLTIP}>
        <Button disabled>원본 CSV 다운로드</Button>
      </Tooltip>
      <Tooltip title={COMING_SOON_TOOLTIP}>
        <Button disabled>실패 건 다운로드</Button>
      </Tooltip>
      {send.canCancel && canCancel ? (
        <Popconfirm
          title="예약 취소"
          description="해당 예약을 취소하시겠습니까?"
          okText="취소 요청"
          cancelText="닫기"
          okButtonProps={{ danger: true, loading: cancelMutation.isLoading }}
          // Promise 를 반환해 완료까지 확인 버튼 로딩 유지. 실패 메시지는 mutation onError 가 노출한다.
          onConfirm={() => cancelMutation.mutateAsync(send.id).catch(() => undefined)}
          disabled={cancelMutation.isLoading}
        >
          <Button danger disabled={cancelMutation.isLoading}>
            예약 취소
          </Button>
        </Popconfirm>
      ) : null}
    </Space>
  );

  return (
    <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
      {failureView === 'banner' ? (
        <Alert type="warning" showIcon closable message={getRefetchFailureMessage(isInProgressStatus(send.status))} />
      ) : null}

      <Row justify="space-between" align="middle">
        <Col>
          <Space align="center">
            <Typography.Title level={4} style={{ margin: 0 }}>
              예약 #{send.id}
            </Typography.Title>
            <AlimtalkSendStatusTag status={send.status} isTest={send.isTest} />
          </Space>
        </Col>
        <Col>{topActions}</Col>
      </Row>

      {/* 발송 요약 */}
      <Card title="발송 요약" size="small">
        <Descriptions column={{ xs: 1, sm: 2, lg: 3 }} bordered size="small">
          <Descriptions.Item label="예약ID">{send.id}</Descriptions.Item>
          <Descriptions.Item label="상태">
            <AlimtalkSendStatusTag status={send.status} isTest={send.isTest} />
          </Descriptions.Item>
          <Descriptions.Item label="발송유형">{formatSendType(send.sendType)}</Descriptions.Item>
          <Descriptions.Item label="예약일시">{formatDateTime(send.scheduledAt)}</Descriptions.Item>
          <Descriptions.Item label="등록자">{send.admin?.name ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="테스트 번호">{send.testPhoneMasked ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="메모" span={3}>
            {send.memo || '-'}
          </Descriptions.Item>
          {send.errorMessage ? (
            <Descriptions.Item label="작업 오류" span={3}>
              <Typography.Text type="danger">{send.errorMessage}</Typography.Text>
            </Descriptions.Item>
          ) : null}
        </Descriptions>
      </Card>

      {/* 처리 결과 + 성공률 */}
      <Row gutter={16}>
        <Col xs={24} lg={14}>
          <Card title="처리 결과" size="small" style={{ height: '100%' }}>
            <Row gutter={16}>
              <Col span={6}>
                <Statistic title="대상모수" value={formatCount(send.targetCount)} />
              </Col>
              <Col span={6}>
                <Statistic title="처리완료" value={formatCount(result.processedCount)} />
              </Col>
              <Col span={6}>
                <Statistic title="성공" value={formatCount(result.successCount)} valueStyle={{ color: '#52c41a' }} />
              </Col>
              <Col span={6}>
                <Statistic title="실패" value={formatCount(result.failedCount)} valueStyle={{ color: '#ff4d4f' }} />
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="발송 성공률" size="small" style={{ height: '100%' }}>
            <AlimtalkSendResultDonutChart successCount={result.successCount} failedCount={result.failedCount} />
          </Card>
        </Col>
      </Row>

      {/* 발송 정보 — ID·상태·유형·예약일시는 발송 요약에 있어 중복 표기하지 않는다 */}
      <Card title="발송 정보" size="small">
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="발급쿠폰">
            {send.coupons.length > 0 ? (
              <Space direction="vertical" size={2}>
                {send.coupons.map((coupon) => (
                  <span key={coupon.id}>{formatCoupon(coupon)}</span>
                ))}
              </Space>
            ) : (
              '-'
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 실패 사유 */}
      <Card title="실패 사유" size="small">
        {/* 스펙: failureReasons 건수의 합은 failedCount 와 같다고 가정하지 않는다 — 숫자 불일치를 오류로 오해하지 않게 안내 */}
        <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
          {FAILURE_REASON_NOTICE}
        </Typography.Paragraph>
        <Table<AlimtalkSendFailureReason>
          rowKey="code"
          size="small"
          columns={failureColumns}
          dataSource={result.failureReasons}
          pagination={false}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="실패 사유 집계 없음" /> }}
        />
      </Card>
    </Space>
  );
};

export default AlimtalkSendHistoryDetailWrap;
